import { useBodyScrollLock } from "../../hooks/useBodyScrollLock";

interface ConfirmModalProps {
  title: string;
  description: string;
  items?: string[];
  confirmLabel?: string;
  confirmClass?: string;
  irreversible?: boolean;
  busy?: boolean;
  busyLabel?: string;
  onConfirm: () => void;
  onCancel: () => void;
  /** Optional extra content between the warning items and the "cannot be
   *  undone" line — e.g. a form field the confirming action needs (the
   *  legal Publish modal's test-recipients input). Existing callers are
   *  unaffected since this is optional. */
  children?: React.ReactNode;
}

const ConfirmModal: React.FC<ConfirmModalProps> = ({
  title,
  description,
  items,
  confirmLabel = "Confirm",
  confirmClass = "bg-red-600 hover:bg-red-500",
  irreversible = true,
  busy = false,
  busyLabel = "Working…",
  onConfirm,
  onCancel,
  children,
}) => {
  useBodyScrollLock();
  return (
  <div
    className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 backdrop-blur-sm"
    onClick={(e) => e.stopPropagation()}
  >
    <div className="bg-gray-900 border border-gray-700 rounded-2xl p-6 w-full max-w-md shadow-2xl">
      <div className="flex items-start gap-3 mb-4">
        <div className="w-10 h-10 rounded-full bg-red-500/10 flex items-center justify-center shrink-0">
          <svg className="w-5 h-5 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
          </svg>
        </div>
        <div>
          <h2 className="text-lg font-semibold text-white">{title}</h2>
          <p className="text-sm text-gray-400 mt-0.5">{description}</p>
        </div>
      </div>
      {items && items.length > 0 && (
        <ul className="space-y-1.5 mb-4">
          {items.map((item) => (
            <li key={item} className="flex items-start gap-2 text-xs text-gray-400">
              <span className="text-red-400 mt-0.5 shrink-0">✕</span>{item}
            </li>
          ))}
        </ul>
      )}
      {children && <div className="mb-4">{children}</div>}
      {irreversible && (
        <p className="text-xs text-red-400 font-medium mb-4">This action cannot be undone.</p>
      )}
      <div className="flex justify-end gap-3">
        <button
          onClick={onCancel}
          disabled={busy}
          className="px-4 py-2 bg-gray-800 text-gray-400 rounded-xl hover:bg-gray-700 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Cancel
        </button>
        <button
          onClick={onConfirm}
          disabled={busy}
          className={`px-4 py-2 text-white rounded-xl transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed ${confirmClass}`}
        >
          {busy ? busyLabel : confirmLabel}
        </button>
      </div>
    </div>
  </div>
  );
};

export default ConfirmModal;
